




















import { Component, Vue, Prop, Ref, Emit, Watch } from 'vue-property-decorator';
// import { Action } from 'vuex-class';
import Moveable from 'vue-moveable';
import { OnDrag, OnDragStart, OnResize, OnRotateStart, OnRotate } from 'moveable';
import { Element as CanvasElement, ElementActionsKeys, ElementActionsPayload } from '@/store/types/element';
import { Action, Getter } from 'vuex-class';
import { RootGettersReturns } from '../../../store/types';
import { ActiveItem } from '../../../store/types/interface';
import { SessionStatus } from '@/store/types/session';

export interface MoveableParams {
  draggable?: boolean;
  throttleDrag?: number;
  resizable?: boolean;
  throttleResize?: number;
  keepRatio?: boolean;
  scalable?: boolean;
  throttleScale?: number;
  rotatable?: boolean;
  throttleRotate?: number;
  pinchable?: boolean;
  origin?: boolean;
  rootContainer?: Element | null;
  zoom?: number;
}

@Component({
  components: {
    Moveable,
  },
})
export default class MoveableComponent extends Vue {
  @Prop({ type: Number, required: true }) id!: number;
  @Prop({ type: Number, required: true }) x!: number;
  @Prop({ type: Number, required: true }) y!: number;
  @Prop({ type: Number, required: true }) width!: number;
  @Prop({ type: Number, required: true }) height!: number;
  @Prop({ type: Number, required: true }) rotation!: number;
  @Prop({ type: Function, required: true }) getZoom!: () => number;
  @Prop({ type: Boolean, required: true }) interactiveProp!: boolean;
  @Prop({ type: Object }) element!: CanvasElement;

  @Action(ElementActionsKeys.UPDATE_ELEMENT) updateElement!: (payload: ElementActionsPayload['UPDATE_ELEMENT']) => void;
  @Action(ElementActionsKeys.SELECT_ELEMENT) selectElement!: (payload: ElementActionsPayload['SELECT_ELEMENT']) => void;
  @Action(ElementActionsKeys.SET_MANIPULATING_ELEMENT) setManipulating!: (
    payload: ElementActionsPayload['SET_MANIPULATING_ELEMENT']
  ) => void;
  // @Action('removeElement') removeElement!: (a: { payload: RemoveElementPayload }) => void;

  @Ref('moveableEl') moveableEl?: Vue;
  @Getter isDoubleSelected!: RootGettersReturns['isDoubleSelected'];
  @Getter isGuest!: RootGettersReturns['isGuest'];
  @Getter interfaceActiveItem!: RootGettersReturns['interfaceActiveItem'];
  @Getter sessionStatus!: RootGettersReturns['sessionStatus'];
  @Getter selectedElement!: RootGettersReturns['selectedElement'];
  @Getter currentUser!: RootGettersReturns['currentUser'];
  @Getter sharedBoard!: RootGettersReturns['sharedBoard'];

  private rect = {
    x: this.x,
    y: this.y,
    width: this.width,
    height: this.height,
    rotation: this.rotation,
  };

  @Watch('x')
  updateX = (newValue: number) => (this.rect.x = newValue);

  @Watch('y')
  updateY = (newValue: number) => (this.rect.y = newValue);

  @Watch('width')
  updateWidth = (newValue: number) => (this.rect.width = newValue);

  @Watch('height')
  updateHeight = (newValue: number) => (this.rect.height = newValue);

  @Watch('rotation')
  updateRotation = (newValue: number) => {
    this.rect.rotation = newValue;
  };

  @Watch('selectedElement')
  updateSelectedElement = (newValue: RootGettersReturns['selectedElement']) => {
    if (newValue && newValue.id !== this.element.id) {
      if (!this.doubleSelected) {
        this.setSelected(false);
      }
    }
  };

  private isInteractive = true;

  private moveableDefaults: MoveableParams = {
    draggable: true,
    throttleDrag: 1,
    resizable: true,
    throttleResize: 1,
    keepRatio: false,
    scalable: false,
    throttleScale: 1,
    rotatable: true,
    throttleRotate: 1,
    pinchable: true, // ["draggable", "resizable", "scalable", "rotatable"]
    origin: false,
  };

  public moveableComponent: MoveableParams = {};

  get isShared() {
    return (this.element.data as { shared: boolean }).shared;
  }

  get isLocked() {
    return (
      (this.element.data as { usedBy: number | boolean }).usedBy &&
      (this.element.data as { usedBy: number }).usedBy !== this.currentUser?.id
    );
  }

  get moveable() {
    return this.isInteractive &&
      !this.sharedBoard &&
      (!this.isGuest || (this.isShared && !this.isLocked)) &&
      this.interactiveProp
      ? {
          ...this.moveableDefaults,
          ...this.moveableComponent,
        }
      : {};
  }

  get styleParams() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      zIndex: (this.element.data as any).zIndex,
      border: this.sessionStatus != SessionStatus.STARTED && !this.sharedBoard ? '1px solid #ccc' : '',
      width: this.width ? `${this.width}px` : 'auto',
      height: this.height ? `${this.height}px` : 'auto',
      transform: `translate3d(${this.x}px, ${this.y}px, 0) rotate3d(0, 0, 1, ${this.rotation}deg)`,
    };
  }

  private selected = false;
  private doubleSelected = false;
  private dragging = false;

  @Emit('selected')
  setSelected(selected: boolean) {
    if (
      this.isGuest &&
      (['Video', 'Audio', 'Image', 'Drawing'].includes(this.element.type) ||
        (this.element.type === 'Text' && (!this.isShared || this.isLocked)))
    )
      return null;
    if (this.dragging) return null;
    if (selected) {
      if (this.selectedElement?.id !== this.id) this.selectElement(this.element);
    } else {
      this.doubleSelected = false;
    }

    if (this.selected && selected) {
      this.doubleSelected = true;
      return null;
    }

    if (this.sharedBoard && selected) {
      this.doubleSelected = true;
    }

    this.setVisibleControlBox(!this.sharedBoard && selected, this.moveableEl);

    this.selected = selected;
    return this.selected;
  }

  created() {
    this.$root.$on('select', this.updatedSelected);
    this.$root.$on('zoom', this.onZoom);
    this.$root.$on('zoomEnd', this.onZoomEnd);
    this.$root.$on('cancelDouble', this.cancelDouble);
    window.addEventListener('keydown', this.destroyElement);
    this.$on('changeMoveable', this.setMoveable);
    this.$on('updateMoveableComponent', this.updateMoveableComponent);
  }

  cancelDouble() {
    this.doubleSelected = false;
  }

  async onZoom() {
    // if (this.selected) {
    const interval = setInterval(() => {
      if (this.updateMoveable) this.updateMoveable();
    }, 40);
    setTimeout(() => {
      if (this.updateMoveable) this.updateMoveable();
      clearInterval(interval);
    }, 200);
    // }
  }

  onZoomEnd() {
    if (this.updateMoveable) this.updateMoveable();
  }

  mounted() {
    this.setVisibleControlBox(false, this.moveableEl);
  }

  updated() {
    this.updateMoveable();
  }

  updateMoveable() {
    if (!this.moveableEl) return;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    (this.moveableEl as any).updateRect();
    (this.moveableEl as any).updateTarget();
    /* eslint-enable @typescript-eslint/no-explicit-any */
  }

  beforeDestroy() {
    this.$root.$off('select', this.updatedSelected);
    this.$root.$off('zoom', this.onZoom);
    this.$root.$off('zoomEnd', this.onZoomEnd);
    this.$root.$off('cancelDouble', this.cancelDouble);
    window.removeEventListener('keydown', this.destroyElement);
    this.$off('changeMoveable', this.setMoveable);
    this.$off('updateMoveableComponent', this.updateMoveableComponent);
  }

  setMoveable(interactive: boolean) {
    this.isInteractive = interactive;
  }

  updateMoveableComponent(newOptions: MoveableParams) {
    this.moveableComponent = {
      ...this.moveableComponent,
      ...newOptions,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setVisibleControlBox(visible: boolean, el: any) {
    el.moveable.innerMoveable.moveable.moveable.controlBox.element.style.display = visible ? 'block' : 'none';
  }

  updatedSelected(target: HTMLElement | CanvasElement) {
    if (target instanceof HTMLElement || target instanceof SVGElement) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controlBox = (this.$refs.moveableEl as any)?.moveable.innerMoveable.moveable.moveable.controlBox.element;
      this.setSelected((this.$refs.moveableEl as Vue)?.$el.contains(target) || controlBox?.contains(target));
    } else {
      this.setSelected(target?.id === this.element.id);
      if (this.element.type === 'Text' && target?.id === this.element.id) {
        this.doubleSelected = true;
      }
    }
  }

  destroyElement(e: KeyboardEvent) {
    // 46 = delete | 8 = backspace
    if (this.selected && (e.keyCode === 46 || e.keyCode === 8) && !this.isDoubleSelected) {
      this.updateElement({ ...this.element, deleted: true });
    }
  }
  /*
   * Drag
   */

  handleDragStart({ set, target, inputEvent }: OnDragStart) {
    this.setManipulating(true);
    this.applyControlBoxTransform();
    inputEvent.stopPropagation();
    this.applyTransform(target);
    set([this.x, this.y]);
    this.dragging = true;
    this.updateMoveable();
    (this.$refs.moveableEl as Vue)?.$el.getClientRects();
  }

  handleDrag({ target, beforeTranslate }: OnDrag) {
    if (this.isGuest || this.sharedBoard) return;
    this.rect.x = beforeTranslate[0];
    this.rect.y = beforeTranslate[1];
    this.applyTransform(target);
  }

  handleDragEnd() {
    this.dragging = false;
    this.setManipulating(false);
    if (Math.abs(this.rect.x - this.element.x) < 10 && Math.abs(this.rect.y - this.element.y) < 10) {
      if (![ActiveItem.TEXT, ActiveItem.FREE_HAND].includes(this.interfaceActiveItem)) {
        this.setSelected(true);
      } else if ([ActiveItem.TEXT].includes(this.interfaceActiveItem) && this.element.type === 'Text') {
        this.setSelected(true);
      }
    } else {
      this.updateElement({ ...this.element, ...this.rect });
    }
  }

  /*
   * Resize
   */

  handleResizeStart({ target }: OnDragStart) {
    this.setVisibleControlBox(false, this.moveableEl);
    this.setManipulating(true);
    this.updateMoveable();
    this.applyTransform(target);
    // this.applyControlBoxTransform(this.rect.x, this.rect.y);
    setTimeout(() => {
      this.setVisibleControlBox(true, this.moveableEl);
    }, 30);
  }

  handleResize({ target, width, height }: OnResize) {
    this.updateMoveable();
    this.rect.width = width;
    this.rect.height = height;
    target.style.width = `${width}px`;
    target.style.height = `${height}px`;
    this.applyControlBoxTransform(this.rect.x, this.rect.y);
    this.applyTransform(target);
  }

  handleResizeEnd() {
    this.setVisibleControlBox(false, this.moveableEl);

    this.setManipulating(false);
    this.applyControlBoxTransform();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.moveableEl as any).updateRect();
    this.updateElement({ ...this.element, ...this.rect });
    setTimeout(() => {
      this.setVisibleControlBox(true, this.moveableEl);
    }, 30);
  }

  /*
   * Rotate
   */
  handleRotateStart({ set }: OnRotateStart) {
    this.setManipulating(true);
    this.applyControlBoxTransform();
    set(this.rect.rotation);
  }

  handleRotate({ beforeRotate, target }: OnRotate) {
    this.rect.rotation = beforeRotate;
    this.applyTransform(target);
  }

  handleRotateEnd() {
    this.setManipulating(false);
    this.updateElement({ ...this.element, ...this.rect });
  }

  applyTransform(target: HTMLElement | SVGElement) {
    target.style.transform = `translate3d(${this.rect.x}px, ${this.rect.y}px, 0) rotate3d(0,0,1,${this.rect.rotation}deg)`;
  }

  applyControlBoxTransform(x?: number, y?: number) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const controlBox: HTMLElement = (this.$refs.moveableEl as any)?.moveable.innerMoveable.moveable.moveable.controlBox
      .element;
    const zoom = this.getZoom();
    const left = x ? `${x * zoom * -1 - (this.rect.width / 2) * zoom}px` : '';
    const top = y ? `${y * zoom * -1 - (this.rect.height / 2) * zoom}px` : '';
    controlBox.style.left = left;
    controlBox.style.top = top;
  }
}
